import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AddIcon from '@mui/icons-material/Add'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice'
import LockIcon from '@mui/icons-material/Lock'
import RestoreFromTrashRoundedIcon from '@mui/icons-material/RestoreFromTrashRounded'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import TagIcon from '@mui/icons-material/Tag'
import { useEdgeFadeScroll } from '../../hooks/useEdgeFadeScroll'
import { useChat } from '../../context/useChat'
import { useAuth } from '../../context/useAuth'
import { useSubscription } from '../../context/useSubscription'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { isWorkspaceOwner } from '../../utils/workspaceOwners'
import { replyWindow } from '../../utils/whatsappChat'
import { useTimeline } from '../../context/useTimeline'
import { limitsLiftedForWorkspace } from '../../utils/demoUnlock'
import { isHelperOnlyAccount } from '../../utils/helperMembership'
import type { ChatMember } from '../../context/chatContextCore'
import type { Channel, ChatAttachment, ChatAuthorRole, Message } from '../../types/chat'
import type { EntityReference } from '../../types/entity'
import type { ChecklistItem } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import { MentionPopover, type MentionProjectItem } from './MentionPopover'
import { parseMentionTrigger } from './mentionParser'
import { ChatCommandBubble } from './ChatCommandBubble'
import { CreateChannelModal } from './CreateChannelModal'
import { MessageBubble } from './MessageBubble'
import { ArtifactMarkerChipRow } from './ArtifactMarkerChip'
import { PresenceStack } from './PresenceStack'
import { ConnectivityPill } from './ConnectivityPill'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useChatControls } from '../../hooks/useChatControls'
import { useOptionalChatSelection } from '../../context/chatSelectionContextCore'
import { buildMessageToken, buildTagToken } from '../../types/aiChatSource'
import { useNls } from '../../nls/nlsProvider'
import { AppDialog, type AppDialogAction } from '../Common/AppDialog'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { MiniLoader } from '../Common/MiniLoader'
import { ControlNotice, EmptyHubState, HiddenFileInput, RailPageShell, Select, RailChipTabs, TextInput, Textarea } from '../Common'
import { historyCutoff, requestChatHistoryUpgrade } from '../../services/chatQuotaService'
import { AttachmentPreviewDialog, type AttachmentPreviewItem } from '../Common/AttachmentPreviewDialog'
import { canAnnotateAttachmentPreviewItem, inferAttachmentPreviewKind } from '../Common/attachmentPreviewHelpers'
import { toConsolePath } from '../../utils/consoleRoutes'
import { writeDesignImportDraft } from '../Design/designImportHandoff'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import {
  canShowCreateChannelButton,
  getChannelEmptyCopy,
  getChatChannelListModeTabs,
  getChatRoleLabel,
  getChatShellCopy,
  getChannelRailHeading,
  getChannelRowActions,
  getComposerPlaceholder,
  getEmptyChannelMessage,
  type TeamChatPlatform,
} from './chatShellChrome'

type SpeechRecognitionResultLike = {
  readonly isFinal: boolean
  readonly [index: number]: { transcript: string }
}

type SpeechRecognitionEventLike = Event & {
  readonly resultIndex: number
  readonly results: ArrayLike<SpeechRecognitionResultLike>
}

type SpeechRecognitionLike = EventTarget & {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: Event) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
}

const ROLE_TEXT_COLOR: Record<ChatAuthorRole, string> = {
  ADMIN: 'text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]',
  MEMBER: 'text-[var(--chart-2)]',
  VIEWER: 'text-[var(--muted)]',
  HELPER: 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]',
  BURNER: 'text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]',
}

const HIDE_SCROLLBAR = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
const MAX_ATTACHMENTS = 5
const HASHTAG_PATTERN = /(^|\s)#([A-Za-z][A-Za-z0-9_-]*)/g

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const TASK_TAG_PATTERN = /#Task-[A-Za-z0-9]+/g
const CHAT_BOT_IDENTITY: string | null = import.meta.env.VITE_CHAT_BOT_IDENTITY ?? null

const extractTaskIds = (text: string): string[] => {
  const matches = text.match(TASK_TAG_PATTERN)
  if (!matches) return []
  return Array.from(new Set(matches.map((tag) => tag.slice(1))))
}

const extractHashtags = (text: string): string[] => {
  const tags = new Set<string>()
  for (const match of text.matchAll(HASHTAG_PATTERN)) {
    tags.add(match[2].toLowerCase())
  }
  return Array.from(tags)
}

const replaceMentionTrigger = (text: string, trigger: NonNullable<ReturnType<typeof parseMentionTrigger>>, label: string) => {
  const before = text.slice(0, trigger.start)
  const after = text.slice(trigger.start).replace(/^[@/][^\s@/]*$/, '')
  const prefix = before && !/\s$/.test(before) ? `${before} ` : before
  const suffix = after && !/^\s/.test(after) ? ` ${after}` : after
  return `${prefix}${label}${suffix}`.replace(/[ \t]{2,}/g, ' ')
}

// Workspace items (elements, checklist items) use `/Title` in the composer
// draft — `#` is reserved for user-typed tags so an item title never gets
// double-counted as a hashtag by HASHTAG_PATTERN.
const getReferenceDraftToken = (reference: EntityReference): string => {
  const title = typeof reference.snapshot.title === 'string' && reference.snapshot.title.trim()
    ? reference.snapshot.title.trim()
    : reference.entityId
  return `${reference.entityType === 'USER' ? '@' : '/'}${title}`
}

const draftIncludes = (text: string, token: string) => text.toLowerCase().includes(token.toLowerCase())

const fuzzyInOrder = (query: string, value: string) => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  const normalizedValue = value.toLowerCase()
  let cursor = 0
  for (const char of normalizedQuery) {
    cursor = normalizedValue.indexOf(char, cursor)
    if (cursor === -1) return false
    cursor += 1
  }
  return true
}

const timestampMillis = (timestamp: unknown): number => {
  if (timestamp && typeof timestamp === 'object' && 'toMillis' in timestamp) {
    try {
      return (timestamp as { toMillis: () => number }).toMillis()
    } catch {
      return 0
    }
  }
  return 0
}

export interface TeamGroupChatProps {
  platform?: TeamChatPlatform
  onSendToAi?: (text: string) => void
  onElementMentionClick?: (elementId: string) => void
  helperSyncState?: string
  helperQuickActions?: {
    focusedLabel?: string
    items?: Array<{ id: string; title: string; subtitle?: string | null }>
    selectedItemId?: string | null
    onSelectItem?: (itemId: string) => void
    onEvidence?: (file: File) => Promise<void> | void
    onScan?: () => Promise<void> | void
    onDone?: () => Promise<void> | void
    onComment?: (text: string) => Promise<void> | void
  }
  renderHelperSummary?: (messages: Message[], channelId: string) => ReactNode
  onTranslateMessage?: (message: Message) => Promise<string>
  /** When true, render only the middle conversation column (messages + composer + dialogs).
   * Left channel rail, right member/AI aside, top bar, and AI-controls panel are skipped;
   * the outer RailPageShell wrapper is also skipped so the caller supplies its own shell.
   * Entity mentions route to `onElementMentionClick` since the right sidecar is not owned here. */
  chromeless?: boolean
}

export const TeamGroupChat = ({ platform = 'desktop', onElementMentionClick, helperSyncState, helperQuickActions, renderHelperSummary, onTranslateMessage, chromeless = false }: TeamGroupChatProps = {}) => {
  const {
    activeChannelId,
    channelsList,
    messagesStream,
    workspaceQuota,
    members,
    viewerPresence,
    unreadByChannel,
    setActiveChannelId,
    sendMessage,
    markRead,
    setTyping,
    deleteChannel,
    restoreChannel,
    permanentlyDeleteArchivedChannel,
    pendingCreateChannelDraft,
    clearCreateChannelDraft,
    editMessage,
    deleteMessage,
    restoreMessage,
  } = useChat()
  const { elements, checklistItems, updateElement, updateChecklistItem, canPerformOnNode } = useTimeline()
  const { currentUser, userProfile } = useAuth()
  const { activeRole, activeWorkspaceId, canPerform, workspaces } = useWorkspace()
  const isHelper = isHelperOnlyAccount({ userProfile, currentUser, workspaces, activeRole }) || platform === 'helper'
  const { t } = useNls()
  const chatSelection = useOptionalChatSelection()
  const { plan } = useSubscription()
  const { showToast } = useToast()
  const canSelectImageMessages = limitsLiftedForWorkspace(workspaces.find((workspace) => workspace.id === activeWorkspaceId)) || plan !== 'FREE_TIER'
  const messageHasImage = (message: Message) =>
    (message.attachments ?? []).some((attachment) => {
      const type = attachment.contentType ?? ''
      const name = attachment.name ?? ''
      return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic)$/i.test(name)
    })
  const requestSelectMessage = (message: Message) => {
    if (!activeChannel) return
    if (messageHasImage(message) && !canSelectImageMessages) {
      showToast('Selecting messages with images is a premium feature. Upgrade your plan to include image chats in AI Magic.', 'warning')
      return
    }
    chatSelection?.toggleToken(buildMessageToken(message, activeChannel))
  }
  const currentUserDisplayName = useDisplayName(userProfile ?? currentUser)
  const currentProfileImageUrl = resolveProfileImageUrl(userProfile, currentUser)
  const currentUserRole: ChatAuthorRole =
    activeRole === 'OWNER' || activeRole === 'ADMIN' ? 'ADMIN' : activeRole === 'VIEWER' ? 'VIEWER' : activeRole === 'HELPER' ? 'HELPER' : 'MEMBER'

  const [textInput, setTextInput] = usePersistedState<string>('chat.textInput', '')
  const [attachments, setAttachments] = useState<File[]>([])
  const [mentionedElementIds, setMentionedElementIds] = usePersistedState<string[]>('chat.mentionedElementIds', [])
  const [mentionedUserIds, setMentionedUserIds] = usePersistedState<string[]>('chat.mentionedUserIds', [])
  const [entityReferences, setEntityReferences] = usePersistedState<EntityReference[]>('chat.entityReferences', [])
  const [selectedTag, setSelectedTag] = usePersistedState<string | null>('chat.selectedTag', null)
  const [channelFilter, setChannelFilter] = usePersistedState<string>('chat.channelFilter', '')
  const [tagFilter, setTagFilter] = usePersistedState<string>('chat.tagFilter', '')
  const [failedLocalMessages, setFailedLocalMessages] = useState<Message[]>([])
  const failedLocalMessageSeq = useRef(0)
  const [isSending, setIsSending] = useState(false)
  const [isWarningDismissed, setIsWarningDismissed] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [mobilePane, setMobilePane] = usePersistedState<'left' | 'main' | 'right'>('chat.mobilePane', 'main')
  const [isAiControlsOpen, setIsAiControlsOpen] = usePersistedState<boolean>('chat.isAiControlsOpen', false)
  const [isDictating, setIsDictating] = useState(false)
  const [isRecordingAudio, setIsRecordingAudio] = useState(false)
  const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)
  const [showChannelArchive, setShowChannelArchive] = usePersistedState<boolean>('chat.showChannelArchive', false)
  const [translations, setTranslations] = useState<Record<string, string>>({})
  const [translatingId, setTranslatingId] = useState<string | null>(null)
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false)
  const [commentPromptOpen, setCommentPromptOpen] = useState(false)
  const [commentPromptInput, setCommentPromptInput] = useState('')
  const [pendingDeleteChannel, setPendingDeleteChannel] = useState<Channel | null>(null)
  const [pendingPermanentDeleteChannel, setPendingPermanentDeleteChannel] = useState<Channel | null>(null)
  const [editingMessage, setEditingMessage] = useState<Message | null>(null)
  const [editingMessageText, setEditingMessageText] = useState('')
  const [replyToId, setReplyToId] = useState<string | null>(null)
  const [previewItem, setPreviewItem] = useState<AttachmentPreviewItem | null>(null)
  const [selectedEntityReference, setSelectedEntityReference] = useState<EntityReference | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const helperEvidenceInputRef = useRef<HTMLInputElement>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<BlobPart[]>([])

  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [activeWorkspaceId, workspaces],
  )

  const openAttachmentPreview = (attachment: ChatAttachment) => {
    setPreviewItem({
      id: attachment.id,
      fileId: attachment.id,
      label: attachment.name,
      kind: inferAttachmentPreviewKind({ name: attachment.name, contentType: attachment.contentType }),
      mimeType: attachment.contentType,
      sizeLabel: formatFileSize(attachment.size),
      url: attachment.downloadUrl,
      downloadUrl: attachment.downloadUrl,
      summary: t('NLSChatTextSharedInChat'),
      isUnavailable: attachment.isUnavailable,
      unavailableReason: attachment.unavailableReason,
    })
  }

  const annotatePreviewItem = (item: AttachmentPreviewItem, sourceUrl: string) => {
    if (!activeWorkspace || !canAnnotateAttachmentPreviewItem(item)) return
    writeDesignImportDraft({
      id: item.id,
      name: item.label,
      url: sourceUrl,
      mimeType: item.mimeType,
      kind: item.kind,
      createdAt: Date.now(),
    })
    setPreviewItem(null)
    window.history.pushState(null, '', toConsolePath(`/workspace/${activeWorkspace.slug}/design`))
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  const participantUids = useMemo(() => {
    const next = new Set(members.map((member) => member.uid).filter(Boolean))
    if (currentUser?.uid) next.add(currentUser.uid)
    return next
  }, [currentUser?.uid, members])
  const canUseChat = isHelper || participantUids.size >= 2

  const hasQuotaConfig = workspaceQuota !== null && workspaceQuota.limit > 0
  const pendingQuotaUnits = 1 + attachments.length
  const quotaUsageRatio = hasQuotaConfig
    ? workspaceQuota.usedToday / workspaceQuota.limit
    : 0
  const isQuotaExhausted = Boolean(
    workspaceQuota?.isOverQuota ||
    (hasQuotaConfig && workspaceQuota.usedToday + pendingQuotaUnits > workspaceQuota.limit)
  )
  const shouldShowWarning = hasQuotaConfig && quotaUsageRatio >= 0.8 && !isQuotaExhausted && !isWarningDismissed

  const tags = useMemo(() => {
    const tagMap = new Map<string, { name: string; lastSeen: number; count: number }>()
    for (const message of messagesStream) {
      const lastSeen = timestampMillis(message.timestamp)
      for (const tag of extractHashtags(message.textContent)) {
        const existing = tagMap.get(tag)
        tagMap.set(tag, {
          name: tag,
          lastSeen: Math.max(existing?.lastSeen ?? 0, lastSeen),
          count: (existing?.count ?? 0) + 1,
        })
      }
    }
    return Array.from(tagMap.values()).sort((a, b) => b.lastSeen - a.lastSeen || a.name.localeCompare(b.name))
  }, [messagesStream])
  // Helpers only ever see private channels they have been explicitly added to —
  // the workspace-wide (#general / main) chat and any other public rooms stay
  // hidden. Members and desktop admins keep the full list.
  const visibleChannelsList = useMemo(() => {
    if (platform !== 'helper') return channelsList
    const uid = currentUser?.uid
    if (!uid) return []
    return channelsList.filter((channel) => channel.type === 'private' && Boolean(channel.roles?.[uid]))
  }, [channelsList, currentUser?.uid, platform])
  const activeChannels = useMemo(
    () => visibleChannelsList.filter((channel) => !channel.archivedAt && !channel.permanentlyDeletedAt),
    [visibleChannelsList],
  )
  const archivedChannels = useMemo(
    () => visibleChannelsList.filter((channel) => Boolean(channel.archivedAt) && !channel.permanentlyDeletedAt),
    [visibleChannelsList],
  )
  const canViewArchive = canPerform('chat.view_archive')
  const canManageArchive = canPerform('chat.manage_archive')
  const canManageChannels = roleCanPerform(activeRole, 'EDITOR')
  const canCreateChannels = canPerform('chat.create_channel') || canPerform('chat.create_private_channel')
  const chatCopy = useMemo(() => getChatShellCopy(t), [t])
  const channelListModeTabs = useMemo(() => getChatChannelListModeTabs(t), [t])
  const channelListForRail = showChannelArchive && canViewArchive ? archivedChannels : activeChannels
  const filteredChannels = useMemo(
    () => channelListForRail.filter((channel) => fuzzyInOrder(channelFilter, `${channel.name} ${channel.slug}`)),
    [channelFilter, channelListForRail],
  )
  const filteredTags = useMemo(
    () => tags.filter((tag) => fuzzyInOrder(tagFilter, tag.name)),
    [tagFilter, tags],
  )

  const visibleMessages = useMemo(() => {
    if (!selectedTag) return messagesStream
    return messagesStream.filter((message) => extractHashtags(message.textContent).includes(selectedTag))
  }, [messagesStream, selectedTag])
  const renderedMessages = useMemo(() => [...visibleMessages, ...failedLocalMessages], [failedLocalMessages, visibleMessages])
  const activeChannel = useMemo(() => visibleChannelsList.find((channel) => channel.id === activeChannelId) ?? null, [activeChannelId, visibleChannelsList])
  const isOwner = Boolean(
    activeRole === 'OWNER' ||
    (activeWorkspace && currentUser?.uid && isWorkspaceOwner(activeWorkspace, currentUser.uid)),
  )
  const {
    noticeMessage: chatControlNotice,
    canSend: canSendWithControl,
    canEditOwn: canEditOwnMessage,
    canCreateChannel,
    mentionFilter,
  } = useChatControls({
    workspaceId: activeWorkspaceId,
    channel: activeChannel as Record<string, unknown> | null,
    isOwner,
  })
  const showCreateChannelButton = canShowCreateChannelButton(platform, canCreateChannels) && canCreateChannel
  const [mountedAtMs] = useState(() => Date.now())
  const historyDays = workspaceQuota?.historyDays ?? -1
  let showHistoryNotice = false
  if (historyDays > 0) {
    const cutoff = historyCutoff(mountedAtMs, historyDays)
    if (cutoff) {
      const cutoffMs = cutoff.toMillis()
      const createdAt = activeChannel?.createdAt
      if (createdAt) {
        let createdMs: number | null = null
        if (typeof createdAt === 'number') {
          createdMs = Number.isFinite(createdAt) ? createdAt : null
        } else if (typeof (createdAt as { toMillis?: () => number }).toMillis === 'function') {
          createdMs = (createdAt as { toMillis: () => number }).toMillis()
        } else if (createdAt instanceof Date) {
          createdMs = createdAt.getTime()
        } else if (typeof (createdAt as { seconds?: number }).seconds === 'number') {
          createdMs = (createdAt as { seconds: number }).seconds * 1000
        } else if (typeof createdAt === 'string') {
          const parsed = Date.parse(createdAt)
          createdMs = Number.isFinite(parsed) ? parsed : null
        }
        showHistoryNotice = createdMs !== null && createdMs < cutoffMs
      }
    }
  }

  // For helpers: if the active channel is not in the list they can see (e.g. the
  // default #general/main channel the app seeds), redirect them to the first
  // channel they belong to — or clear the selection to show the empty state.
  useEffect(() => {
    if (platform !== 'helper') return
    if (!activeWorkspaceId) return
    if (activeChannelId && visibleChannelsList.some((channel) => channel.id === activeChannelId)) return
    const nextChannelId = activeChannels[0]?.id ?? null
    if (nextChannelId === activeChannelId) return
    setActiveChannelId(nextChannelId)
  }, [activeChannelId, activeChannels, activeWorkspaceId, platform, setActiveChannelId, visibleChannelsList])
  const isActiveChannelArchived = Boolean(activeChannel?.archivedAt || activeChannel?.permanentlyDeletedAt)
  const canSendInActiveChannel = canUseChat && !isActiveChannelArchived && canSendWithControl
  const selectedElement = useMemo(
    () => selectedEntityReference?.entityType === 'ELEMENT'
      ? elements.find((candidate) => candidate.id === selectedEntityReference.entityId) ?? null
      : null,
    [elements, selectedEntityReference],
  )
  const selectedChecklistItem = useMemo(
    () => selectedEntityReference?.entityType === 'CHECKLIST'
      ? checklistItems.find((candidate) => candidate.id === selectedEntityReference.entityId) ?? null
      : null,
    [checklistItems, selectedEntityReference],
  )

  const projectMentionItems = useMemo<MentionProjectItem[]>(() => [
    ...elements.map((element) => ({
      id: element.id,
      title: element.title,
      entityType: 'ELEMENT' as const,
      typeLabel: element.type,
      publicId: element.publicId,
      color: element.color,
    })),
    ...checklistItems.map((item) => ({
      id: item.id,
      title: item.title,
      entityType: 'CHECKLIST' as const,
      typeLabel: 'CHECKLIST',
      publicId: item.publicId ?? null,
      color: null,
    })),
  ], [checklistItems, elements])

  const mentionTrigger = useMemo(() => parseMentionTrigger(textInput), [textInput])
  const mentionMemberMatches = useMemo(() => (!isHelper && mentionTrigger?.kind === 'MEMBER')
    ? members
      .filter((member) => Boolean(member.uid))
      .filter((member) => !mentionedUserIds.includes(member.uid))
      .filter((member) => `${member.name} ${member.email}`.toLowerCase().includes(mentionTrigger.query))
      .filter(mentionFilter)
      .slice(0, 6)
    : [], [isHelper, mentionFilter, mentionTrigger, mentionedUserIds, members])
  const mentionElementMatches = useMemo(() => mentionTrigger?.kind === 'ELEMENT'
    ? projectMentionItems
      .filter((element) => !entityReferences.some((reference) => reference.entityId === element.id && reference.entityType === element.entityType))
      .filter((element) => `${element.title} ${element.publicId ?? ''} ${element.typeLabel}`.toLowerCase().includes(mentionTrigger.query))
      .slice(0, 8)
    : [], [entityReferences, mentionTrigger, projectMentionItems])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [visibleMessages])

  useEffect(() => {
    if (!pendingCreateChannelDraft) return
    setIsCreateChannelOpen(true)
    setMobilePane('left')
  }, [pendingCreateChannelDraft, setMobilePane])

  useEffect(() => {
    if (!hasQuotaConfig || quotaUsageRatio < 0.8) setIsWarningDismissed(false)
  }, [hasQuotaConfig, quotaUsageRatio])

  useEffect(() => () => {
    recognitionRef.current?.abort()
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop()
  }, [])

  const applyMemberMention = (member: ChatMember) => {
    if (!mentionTrigger || mentionTrigger.kind !== 'MEMBER' || !member.uid) return
    setTextInput((current) => replaceMentionTrigger(current, mentionTrigger, `@${member.name}`))
    setMentionedUserIds((current) => current.includes(member.uid) ? current : [...current, member.uid])
    setEntityReferences((current) => current.some((reference) => reference.entityType === 'USER' && reference.entityId === member.uid)
      ? current
      : [...current, { entityType: 'USER', entityId: member.uid, snapshot: { title: member.name, subtitle: member.email || member.role, type: 'USER' } }])
    inputRef.current?.focus()
  }

  const applyElementMention = (element: MentionProjectItem) => {
    if (!mentionTrigger || mentionTrigger.kind !== 'ELEMENT') return
    setTextInput((current) => replaceMentionTrigger(current, mentionTrigger, `/${element.title}`))
    if (element.entityType === 'ELEMENT') setMentionedElementIds((current) => current.includes(element.id) ? current : [...current, element.id])
    setEntityReferences((current) => current.some((reference) => reference.entityType === element.entityType && reference.entityId === element.id)
      ? current
      : [...current, {
          entityType: element.entityType,
          entityId: element.id,
          snapshot: { title: element.title, subtitle: element.typeLabel, color: element.color ?? undefined, type: element.typeLabel },
        }])
    inputRef.current?.focus()
  }

  const appendFiles = (files: FileList | null) => {
    if (!files) return
    setAttachments((current) => {
      const next = [...current, ...Array.from(files)].slice(0, MAX_ATTACHMENTS)
      if (current.length + files.length > MAX_ATTACHMENTS) setErrorMessage(t('NLSChatErrorAttachmentLimit'))
      else setErrorMessage('')
      return next
    })
  }

  const removeAttachment = (index: number) => {
    setAttachments((current) => current.filter((_, position) => position !== index))
  }

  const submit = async (override?: { text?: string; files?: File[] }) => {
    if (!canUseChat) {
      setErrorMessage(t('NLSChatNoticeLocked'))
      return
    }
    if (isActiveChannelArchived) {
      setErrorMessage(t('NLSChatErrorArchivedChannelRestore'))
      return
    }
    if (isQuotaExhausted) {
      setErrorMessage(t('NLSChatErrorDailyQuotaReached'))
      return
    }
    const text = (override?.text ?? textInput).trim()
    const files = override?.files ?? attachments
    if (extractHashtags(text).length > 1) {
      setErrorMessage(t('NLSChatErrorOneTagPerMessage'))
      return
    }
    const activeEntityReferences = entityReferences.filter((reference) => draftIncludes(text, getReferenceDraftToken(reference)))
    const activeMentionedElementIds = mentionedElementIds.filter((elementId) => {
      const reference = activeEntityReferences.find((candidate) => candidate.entityType === 'ELEMENT' && candidate.entityId === elementId)
      if (reference) return true
      const element = elements.find((candidate) => candidate.id === elementId)
      return element ? draftIncludes(text, `/${element.title}`) : false
    })
    const activeMentionedUserIds = mentionedUserIds.filter((userId) => {
      const reference = activeEntityReferences.find((candidate) => candidate.entityType === 'USER' && candidate.entityId === userId)
      if (reference) return true
      const member = members.find((candidate) => candidate.uid === userId)
      return member ? draftIncludes(text, `@${member.name}`) : false
    })
    if (!text && files.length === 0 && activeMentionedElementIds.length === 0 && activeMentionedUserIds.length === 0 && activeEntityReferences.length === 0) return

    setIsSending(true)
    setErrorMessage('')
    try {
      const targetTaskIds = extractTaskIds(text)
      await sendMessage(text, {
        attachments: files,
        ...(replyToId ? { replyToId } : {}),
        ...(targetTaskIds.length > 0 ? { targetTaskIds } : {}),
        ...(activeMentionedElementIds.length > 0 ? { mentionedElementIds: activeMentionedElementIds } : {}),
        ...(activeMentionedUserIds.length > 0 ? { mentionedUserIds: activeMentionedUserIds } : {}),
        ...(activeEntityReferences.length > 0 ? { entityReferences: activeEntityReferences } : {}),
      })
      if (!override) {
        setTextInput('')
        setAttachments([])
        setMentionedElementIds([])
        setMentionedUserIds([])
        setEntityReferences([])
        setReplyToId(null)
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorSendFailed'))
      if (!override && text) {
        setFailedLocalMessages((current) => [...current, {
          id: `failed-${Date.now()}-${failedLocalMessageSeq.current++}`,
          authorId: currentUser?.uid ?? 'local',
          authorName: currentUserDisplayName,
          authorAvatarUrl: currentProfileImageUrl,
          authorRole: currentUserRole,
          textContent: text,
          attachmentCount: 0,
          timestamp: null,
          optimisticStatus: 'failed',
        } as Message])
      }
      console.warn('[chat] sendMessage failed', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void submit()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void submit()
    }
  }

  const toggleDictation = () => {
    if (isDictating) {
      recognitionRef.current?.stop()
      setIsDictating(false)
      return
    }
    const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      setErrorMessage(t('NLSChatErrorDictationUnavailable'))
      return
    }
    const recognition = new SpeechRecognition()
    recognitionRef.current = recognition
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'
    recognition.onresult = (event) => {
      let finalText = ''
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index]
        if (result.isFinal) finalText += result[0].transcript
      }
      if (!finalText.trim()) return
      setTextInput((current) => `${current}${current && !/\s$/.test(current) ? ' ' : ''}${finalText.trim()}`)
    }
    recognition.onerror = () => setIsDictating(false)
    recognition.onend = () => setIsDictating(false)
    recognition.start()
    setIsDictating(true)
  }

  const toggleAudioRecording = async () => {
    if (isRecordingAudio) {
      mediaRecorderRef.current?.stop()
      setIsRecordingAudio(false)
      return
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMessage(t('NLSChatErrorAudioUnavailable'))
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      audioChunksRef.current = []
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop())
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        if (blob.size === 0) return
        const file = new File([blob], `voice-memo-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`, {
          type: blob.type,
        })
        void submit({ text: '', files: [file] })
      }
      mediaRecorderRef.current = recorder
      recorder.start()
      setIsRecordingAudio(true)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorStartAudioFailed'))
      setIsRecordingAudio(false)
    }
  }

  const translateMessage = async (message: Message) => {
    if (!message.textContent.trim() || !onTranslateMessage) return
    setTranslatingId(message.id)
    try {
      const translated = await onTranslateMessage(message)
      setTranslations((current) => ({ ...current, [message.id]: translated }))
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorTranslationFailed'))
    } finally {
      setTranslatingId(null)
    }
  }

  const retryFailedMessage = async (message: Message) => {
    setFailedLocalMessages((current) => current.map((entry) => entry.id === message.id ? { ...entry, optimisticStatus: 'abandoned' } : entry))
    await sendMessage(message.textContent).catch((error) => {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorRetryFailed'))
    })
  }

  const openEditMessageDialog = (message: Message) => {
    setEditingMessage(message)
    setEditingMessageText(message.textContent)
  }

  const submitEditMessage = async () => {
    if (!editingMessage) return
    const next = editingMessageText.trim()
    if (!next || next === editingMessage.textContent.trim()) {
      setEditingMessage(null)
      setEditingMessageText('')
      return
    }
    setErrorMessage('')
    try {
      await editMessage(editingMessage.id, next)
      setEditingMessage(null)
      setEditingMessageText('')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorEditMessageFailed'))
    }
  }

  const handleCommentSubmit = () => {
    const trimmed = commentPromptInput.trim()
    setCommentPromptOpen(false)
    setCommentPromptInput('')
    if (trimmed) {
      void helperQuickActions?.onComment?.(trimmed)
    }
  }

  const commentPromptActions = useMemo<AppDialogAction[]>(() => [
    {
      label: t('NLSCommonActionCancel'),
      variant: 'ghost',
      onClick: () => {
        setCommentPromptOpen(false)
        setCommentPromptInput('')
      },
    },
    {
      label: t('NLSChatActionComment'),
      variant: 'primary',
      disabled: !commentPromptInput.trim(),
      onClick: handleCommentSubmit,
    },
  ], [commentPromptInput, t, helperQuickActions])

  const requestDeleteMessage = async (message: Message) => {
    setErrorMessage('')
    try {
      await deleteMessage(message.id)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorDeleteMessageFailed'))
    }
  }

  const requestRestoreMessage = async (message: Message) => {
    setErrorMessage('')
    try {
      await restoreMessage(message.id)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorRestoreMessageFailed'))
    }
  }

  const hydrateLegacyReferences = (message: Message): Message => {
    if ((message.entityReferences?.length ?? 0) > 0) return message
    const references: EntityReference[] = []
    for (const elementId of message.mentionedElementIds ?? []) {
      const element = elements.find((candidate) => candidate.id === elementId)
      references.push({
        entityType: 'ELEMENT',
        entityId: elementId,
        snapshot: {
          title: element?.title ?? t('NLSChatSidecarProjectItem'),
          subtitle: element?.type ?? t('NLSChatSidecarTimelineItem'),
          color: element?.color,
          type: element?.type,
        },
      })
    }
    for (const userId of message.mentionedUserIds ?? []) {
      const member = members.find((candidate) => candidate.uid === userId)
      references.push({
        entityType: 'USER',
        entityId: userId,
        snapshot: {
          title: member?.name ?? t('NLSChatLabelMembers'),
          subtitle: member?.email || member?.role || t('NLSChatSidecarWorkspaceMember'),
          type: 'USER',
        },
      })
    }
    return references.length > 0 ? { ...message, entityReferences: references } : message
  }

  const openEntityReference = (reference: EntityReference) => {
    if (platform === 'desktop' && !chromeless) {
      setSelectedEntityReference(reference)
      setMobilePane('right')
      return
    }
    if (reference.entityType === 'ELEMENT' || reference.entityType === 'CHECKLIST') {
      onElementMentionClick?.(reference.entityId)
    }
  }

  const selectChannel = (channelId: string) => {
    setActiveChannelId(channelId)
    setSelectedTag(null)
    void markRead(channelId)
  }

  const confirmDeleteChannel = async () => {
    const channel = pendingDeleteChannel
    if (!channel) return
    setErrorMessage('')
    try {
      await deleteChannel(channel.id)
      setPendingDeleteChannel(null)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorChannelRemoveFailed'))
      setPendingDeleteChannel(null)
    }
  }

  const confirmPermanentDeleteChannel = async () => {
    const channel = pendingPermanentDeleteChannel
    if (!channel) return
    setErrorMessage('')
    try {
      await permanentlyDeleteArchivedChannel(channel.id)
      setPendingPermanentDeleteChannel(null)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorPermanentDeleteFailed'))
      setPendingPermanentDeleteChannel(null)
    }
  }

  const restoreArchivedChannel = async (channel: Channel) => {
    setErrorMessage('')
    try {
      await restoreChannel(channel.id)
      setShowChannelArchive(false)
      selectChannel(channel.id)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('NLSChatErrorChannelRestoreFailed'))
    }
  }

  const leftSidebarScroll = useEdgeFadeScroll<HTMLElement>()
  return (
    <ConditionalRailShell wrap={!chromeless} ariaLabel={chatCopy.pageLabel}>
    <div
      className={`teamChatShell ${HIDE_SCROLLBAR} teamChatShell--${platform}${chromeless ? ' teamChatShell--chromeless' : ''}`}
      data-mobile-pane={mobilePane}
    >
      {!chromeless ? (
      <aside className={`teamChatSidebar ${HIDE_SCROLLBAR}`}>
        <header className="teamChatSidebarHeader">
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {chatCopy.sidebarTitle}
          </span>
          <IconButton
            size="sm"
            variant="tertiary"
            className="teamChatPaneClose"
            label={chatCopy.closeChannelsLabel}
            icon={<CloseRoundedIcon fontSize="inherit" />}
            onClick={() => setMobilePane('main')}
          />
        </header>

        <nav ref={leftSidebarScroll.ref} {...leftSidebarScroll.scrollProps} className="teamChatSidebarScroll edgeFadeScroll" style={{ flex: '1 1 auto' }}>
          <section className="teamChatSidebarSection">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <p className="dropdownLabel" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                {getChannelRailHeading(showChannelArchive, canViewArchive, t)}
              </p>
              {showCreateChannelButton ? (
                <IconAction label={chatCopy.createChannelLabel} onClick={() => {
                  setEditingChannel(null)
                  setIsCreateChannelOpen(true)
                }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconAction>
              ) : null}
            </div>
            {canViewArchive ? (
              <RailChipTabs<'active' | 'archive'>
                items={channelListModeTabs.map((tab) => ({ key: tab.id, label: tab.label }))}
                value={showChannelArchive ? 'archive' : 'active'}
                onChange={(key) => setShowChannelArchive(key === 'archive')}
                ariaLabel={chatCopy.channelListModeLabel}
                orientation="horizontal"
                className="teamChatRailSegmented"
              />
            ) : null}
            <TextInput
              size="sm"
              value={channelFilter}
              onChange={setChannelFilter}
              placeholder={chatCopy.channelFilterPlaceholder}
              aria-label={chatCopy.channelFilterAriaLabel}
              leadingIcon={<SearchIcon sx={{ fontSize: 14 }} />}
            />
            {channelListForRail.length === 0 ? (
              <p style={{ padding: '6px 9px', color: 'var(--muted)', fontSize: 11, fontWeight: 850, fontStyle: 'italic' }}>
                {getChannelEmptyCopy(showChannelArchive, t)}
              </p>
            ) : filteredChannels.length === 0 ? (
              <p style={{ padding: '6px 9px', color: 'var(--muted)', fontSize: 11, fontWeight: 850, fontStyle: 'italic' }}>
                {chatCopy.noChannelMatches}
              </p>
            ) : (
              filteredChannels.map((channel) => {
                const rowActions = getChannelRowActions({
                  showChannelArchive,
                  canManageArchive,
                  canManageChannels,
                  channelId: channel.id,
                })
                return (
                  <div
                    key={channel.id}
                    className={`teamChatChannelRowShell${activeChannelId === channel.id ? ' isActive' : ''}`}
                  >
                  <Pressable
                    onClick={() => selectChannel(channel.id)}
                    className={activeChannelId === channel.id ? 'teamChatSidebarRow teamChatSidebarRow--active teamChatSidebarRow--channel' : 'teamChatSidebarRow teamChatSidebarRow--channel'}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {channel.type === 'private' ? <LockIcon sx={{ fontSize: 13 }} /> : <span aria-hidden="true">#</span>}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{channel.slug || channel.name}</span>
                    </span>
                    {unreadByChannel[channel.id] && activeChannelId !== channel.id ? <span aria-label={chatCopy.unreadLabel}>•</span> : null}
                  </Pressable>
                  {showChannelArchive ? (
                    <>
                      {rowActions.canRestore ? (
                        <IconButton
                          size="sm"
                          variant="tertiary"
                          className="teamChatChannelDelete"
                          label={t('NLSChatAriaRestoreChannel', { channel: channel.name })}
                          icon={<RestoreFromTrashRoundedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => { void restoreArchivedChannel(channel) }}
                        />
                      ) : null}
                      {rowActions.canDeleteForever ? (
                        <IconButton
                          size="sm"
                          variant="tertiary"
                          tone="danger"
                          className="teamChatChannelDelete isDanger"
                          label={t('NLSChatAriaDeleteChannelForever', { channel: channel.name })}
                          icon={<DeleteForeverRoundedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => setPendingPermanentDeleteChannel(channel)}
                        />
                      ) : null}
                    </>
                  ) : (
                    <>
                      {rowActions.canEdit ? (
                        <IconButton
                          size="sm"
                          variant="tertiary"
                          className="teamChatChannelDelete"
                          label={t('NLSChatAriaEditChannel', { channel: channel.name })}
                          icon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => {
                            setEditingChannel(channel)
                            setIsCreateChannelOpen(true)
                          }}
                        />
                      ) : null}
                      {rowActions.canArchive ? (
                        <IconButton
                          size="sm"
                          variant="tertiary"
                          tone="danger"
                          className="teamChatChannelDelete isDanger"
                          label={t('NLSChatAriaArchiveChannel', { channel: channel.name })}
                          icon={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => setPendingDeleteChannel(channel)}
                        />
                      ) : null}
                    </>
                  )}
                  </div>
                )
              })
            )}
          </section>

          <section className="teamChatSidebarSection">
            <p className="dropdownLabel" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <TagIcon sx={{ fontSize: 14 }} />
              {chatCopy.tagsHeading}
            </p>
            <TextInput
              size="sm"
              value={tagFilter}
              onChange={setTagFilter}
              placeholder={chatCopy.tagFilterPlaceholder}
              aria-label={chatCopy.tagFilterAriaLabel}
              leadingIcon={<SearchIcon sx={{ fontSize: 14 }} />}
            />
            <Pressable
              onClick={() => setSelectedTag(null)}
              className={selectedTag === null ? 'teamChatSidebarRow teamChatSidebarRow--active' : 'teamChatSidebarRow'}
              aria-pressed={selectedTag === null}
            >
              <span>{chatCopy.allChatsLabel}</span>
              <span>{messagesStream.length}</span>
            </Pressable>
            {tags.length === 0 ? (
              <p style={{ padding: '6px 9px', color: 'var(--muted)', fontSize: 11, fontWeight: 850, fontStyle: 'italic' }}>
                {chatCopy.noHashtags}
              </p>
            ) : filteredTags.length === 0 ? (
              <p style={{ padding: '6px 9px', color: 'var(--muted)', fontSize: 11, fontWeight: 850, fontStyle: 'italic' }}>
                {chatCopy.noTagMatches}
              </p>
            ) : (
              filteredTags.map((tag) => (
                <Pressable
                  key={tag.name}
                  onClick={() => setSelectedTag(tag.name)}
                  className={selectedTag === tag.name ? 'teamChatSidebarRow teamChatSidebarRow--active' : 'teamChatSidebarRow'}
                  aria-pressed={selectedTag === tag.name}
                >
                  <span>#{tag.name}</span>
                  <span>{tag.count}</span>
                </Pressable>
              ))
            )}
          </section>
        </nav>

        <CreateChannelModal
          isOpen={isCreateChannelOpen}
          editChannel={editingChannel}
          initialDraft={pendingCreateChannelDraft}
          onClose={() => {
            setIsCreateChannelOpen(false)
            setEditingChannel(null)
            clearCreateChannelDraft()
          }}
          onCreated={(channelId) => {
            clearCreateChannelDraft()
            selectChannel(channelId)
          }}
        />

        <footer className="teamChatUserFooter">
          {currentProfileImageUrl ? (
            <img src={currentProfileImageUrl} alt="" className="teamChatAvatar" style={{ width: 28, height: 28 }} />
          ) : (
            <span className="teamChatAvatarFallback" style={{ width: 28, height: 28, fontSize: 11 }}>
              {initialsOf(currentUserDisplayName)}
            </span>
          )}
          <div>
            <strong>{currentUserDisplayName}</strong>
            <span>{getChatRoleLabel(t, currentUserRole)}</span>
          </div>
        </footer>
      </aside>
      ) : null}

      <main className="teamChatMain">
        {!chromeless ? (
        <header className="teamChatTopBar">
          <IconButton
            size="md"
            variant="tertiary"
            className="teamChatPaneToggle teamChatPaneToggle--left"
            label={chatCopy.showChannelsLabel}
            icon={<MenuRoundedIcon fontSize="inherit" />}
            onClick={() => setMobilePane('left')}
          />
          <div className="teamChatHeaderMeta">
            <PresenceStack viewers={viewerPresence} onClick={() => setMobilePane('right')} />
            <ConnectivityPill syncState={platform === 'helper' ? helperSyncState : undefined} />
          </div>
          <Button
            size="sm"
            variant="tertiary"
            className="teamChatAiTrigger"
            aria-expanded={isAiControlsOpen}
            aria-controls="teamChatAiPanel"
            icon={<AutoAwesomeRoundedIcon fontSize="inherit" />}
            trailingIcon={
              <span aria-hidden="true" className="teamChatAiTriggerChevron">
                {isAiControlsOpen ? <ExpandLessRoundedIcon fontSize="inherit" /> : <ExpandMoreRoundedIcon fontSize="inherit" />}
              </span>
            }
            onClick={() => setIsAiControlsOpen((current) => !current)}
          >
            {chatCopy.aiTriggerLabel}
          </Button>
          {!isHelper && (
            <IconButton
              size="md"
              variant="tertiary"
              className="teamChatPaneToggle teamChatPaneToggle--right"
              label={chatCopy.showMembersLabel}
              icon={<MoreHorizRoundedIcon fontSize="inherit" />}
              onClick={() => setMobilePane('right')}
            />
          )}
        </header>
        ) : null}

        {!chromeless && isAiControlsOpen && (
          <section
            id="teamChatAiPanel"
            className="teamChatAiPanel"
            aria-label={chatCopy.aiControlsLabel}
          >
            <IconButton
              size="sm"
              variant="tertiary"
              className="teamChatAiPanelClose"
              label={chatCopy.closeAiControlsLabel}
              icon={<CloseRoundedIcon fontSize="inherit" />}
              onClick={() => setIsAiControlsOpen(false)}
            />
            <Button
              size="sm"
              variant="tertiary"
              pressed={chatSelection?.mode === 'select'}
              icon={<AutoAwesomeRoundedIcon fontSize="inherit" />}
              onClick={() => chatSelection?.setMode(chatSelection.mode === 'select' ? 'list' : 'select')}
            >
              {chatSelection?.mode === 'select' ? 'Stop selecting' : chatCopy.selectChatsForAiLabel}
            </Button>
          </section>
        )}

        {platform === 'helper' && activeChannel?.autoSummarizeForHelpers && activeChannelId ? renderHelperSummary?.(visibleMessages, activeChannelId) : null}

        <div className={`teamChatMessageArea ${HIDE_SCROLLBAR}`}>
          {!canUseChat && (
            <div className="teamChatEmptyNotice">
              {chatCopy.lockedNotice}
            </div>
          )}
          {isActiveChannelArchived && (
            <div className="teamChatEmptyNotice">
              {chatCopy.archivedNotice}
            </div>
          )}
          {showHistoryNotice && (
            <ControlNotice
              tone="info"
              message={t('NLSChatHistoryHiddenNotice', { days: historyDays })}
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => requestChatHistoryUpgrade(activeWorkspaceId)}
                >
                  {t('NLSChatActionUpgrade')}
                </Button>
              }
            />
          )}
          {canUseChat && !isActiveChannelArchived && renderedMessages.length === 0 && (
            <div className="teamChatEmptyNotice">
              <EmptyHubState preset="chat" title={getEmptyChannelMessage(platform, t)} compact />
            </div>
          )}
          {renderedMessages.map((message: Message) => {
            const isSelf = message.authorId === currentUser?.uid
            const canModerateMessage = roleCanPerform(activeRole, 'EDITOR')
            const isPersistedMessage = !message.id.startsWith('failed-')
            const parentMessage = message.replyToId
              ? renderedMessages.find((candidate) => candidate.id === message.replyToId) ?? null
              : null
            const replyToPreview = parentMessage
              ? { authorName: parentMessage.authorName, text: parentMessage.textContent, isDeleted: parentMessage.isDeleted === true }
              : null
            return (
              <div key={message.id} className={`chatMessageGroup${isSelf ? ' isSelf' : ''}`}>
                <MessageBubble
                  message={hydrateLegacyReferences(message)}
                  isSelf={isSelf}
                  selectable={chatSelection?.mode === 'select' && Boolean(activeChannel)}
                  selected={activeChannel ? chatSelection?.hasToken(buildMessageToken(message, activeChannel)) : false}
                  onToggleSelected={activeChannel ? () => requestSelectMessage(message) : undefined}
                  onTagClick={(tag) => chatSelection?.toggleToken(buildTagToken(tag, activeChannelId ?? undefined))}
                  onEntityClick={openEntityReference}
                  translation={translations[message.id]}
                  translating={translatingId === message.id}
                  onTranslate={platform === 'helper' && message.authorId !== currentUser?.uid && onTranslateMessage ? () => { void translateMessage(message) } : undefined}
                  onRetry={message.optimisticStatus === 'failed' ? () => { void retryFailedMessage(message) } : undefined}
                  onPreviewAttachment={openAttachmentPreview}
                  canEdit={isPersistedMessage && isSelf && !isActiveChannelArchived && canEditOwnMessage}
                  canDelete={isPersistedMessage && (isSelf || canModerateMessage) && !isActiveChannelArchived}
                  canRestore={isPersistedMessage && (isSelf || canModerateMessage) && !isActiveChannelArchived}
                  onEdit={() => openEditMessageDialog(message)}
                  onDelete={() => { void requestDeleteMessage(message) }}
                  onRestore={() => { void requestRestoreMessage(message) }}
                  replyToPreview={replyToPreview}
                  onReply={isPersistedMessage && !isActiveChannelArchived && canUseChat && !message.isDeleted ? () => setReplyToId(message.id) : undefined}
                  workspaceId={activeWorkspaceId}
                  channelId={activeChannelId ?? undefined}
                  canRetryWhatsapp={Boolean(activeChannel && currentUser && activeChannel.roles?.[currentUser.uid])}
                />
                <div className="min-w-0">
                  {message.metadata?.markers && message.metadata.markers.length > 0 && (
                    <ArtifactMarkerChipRow markers={message.metadata.markers} />
                  )}
                  {message.authorId === CHAT_BOT_IDENTITY && message.metadata?.commands?.map((command) => (
                    <ChatCommandBubble key={command.commandId} command={command} />
                  ))}
                </div>
              </div>
            )
          })}
          <div ref={messagesEndRef} />
        </div>

        <div style={{ marginTop: 'auto' }}>
          <AnimatePresence initial={false}>
            {shouldShowWarning && workspaceQuota && (
              <motion.div
                key="quota-warning"
                initial={{ opacity: 0, y: 15, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="teamChatQuotaWarning"
              >
                <span>
                  {t('NLSChatNoticeQuotaUsage', {
                    percent: Math.round(quotaUsageRatio * 100),
                    used: workspaceQuota.usedToday,
                    limit: workspaceQuota.limit,
                  })}
                </span>
                <IconButton
                  size="sm"
                  variant="tertiary"
                  label={t('NLSChatAriaDismissQuotaWarning')}
                  icon={<CloseRoundedIcon fontSize="inherit" />}
                  onClick={() => setIsWarningDismissed(true)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {(isQuotaExhausted || errorMessage) && (
            <div className="teamChatErrorLine">{errorMessage || t('NLSChatErrorDailyQuotaReached')}</div>
          )}

          {attachments.length > 0 && (
            <div style={{ margin: '0 14px 8px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {attachments.map((file, index) => (
                <Pressable
                  key={`${file.name}-${file.size}-${index}`}
                  onClick={() => removeAttachment(index)}
                  className="teamChatAttachmentChip"
                  disabled={isSending}
                >
                  <AttachFileIcon sx={{ fontSize: 14 }} />
                  <span style={{ maxWidth: '11rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                  {isSending ? <MiniLoader tone="upload" size="xs" label={t('NLSCommonStatusLoading')} inline /> : <span>{t('NLSChatActionRemove')}</span>}
                </Pressable>
              ))}
            </div>
          )}

          {replyToId ? (() => {
            const parent = messagesStream.find((candidate) => candidate.id === replyToId)
            if (!parent) return null
            const preview = (parent.textContent || '').replace(/\s+/g, ' ').trim()
            const capped = preview.length > 120 ? `${preview.slice(0, 119)}…` : preview
            return (
              <div className="teamChatReplyDraft" role="note" aria-label={t('NLSChatAriaReplyingTo', { author: parent.authorName })}>
                <span className="teamChatReplyDraft__bar" aria-hidden="true" />
                <div className="teamChatReplyDraft__body">
                  <span className="teamChatReplyDraft__label">{t('NLSChatLabelReplyingTo', { author: parent.authorName })}</span>
                  <span className="teamChatReplyDraft__preview">
                    {parent.isDeleted ? t('NLSChatMessageDeleted') : capped || t('NLSChatAttachmentGeneric')}
                  </span>
                </div>
                <IconButton
                  size="sm"
                  variant="tertiary"
                  className="teamChatReplyDraft__close"
                  label={t('NLSChatActionCancelReply')}
                  icon={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setReplyToId(null)}
                />
              </div>
            )
          })() : null}

          {chatControlNotice ? (
            <div style={{ margin: '0 14px 8px' }}>
              <ControlNotice
                tone="warning"
                message={chatControlNotice}
                testId="chat-control-notice"
              />
            </div>
          ) : null}

          {activeChannel?.identityType === 'WHATSAPP' && activeChannel.whatsappPaused ? (
            <div className="chatWhatsAppPausedNotice">
              <ControlNotice
                tone="warning"
                message={t('NLSChatNoticePaused')}
                testId="whatsapp-paused-notice"
              />
            </div>
          ) : null}

          {activeChannel?.identityType === 'WHATSAPP' ? (
            <div className="whatsappComposerHints" data-testid="whatsapp-composer-hints">
              {(() => {
                const isTimeliningRoute = activeWorkspace?.settings?.whatsapp?.route !== 'OWN'
                const windowState = replyWindow(activeChannel.whatsappLastInboundAt)
                return (
                  <>
                    {!windowState.open && (
                      <div className="whatsappClosedWindowHint" data-testid="whatsapp-closed-window-hint">
                        {t('NLSChatHintClosedWindow')}
                      </div>
                    )}
                    {isTimeliningRoute && (
                      <div className="whatsappCostHint" data-testid="whatsapp-cost-hint">
                        {t('NLSChatHintCost')}
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          ) : null}

          <form onSubmit={handleFormSubmit} className="teamChatComposer">
            {isSending && (
              <div className="teamChatMiniStatus">
                <MiniLoader tone={attachments.length > 0 ? 'upload' : 'default'} size="sm" label={attachments.length > 0 ? t('NLSCommonStatusLoading') : t('NLSChatActionSendMessage')} />
              </div>
            )}
            {platform !== 'desktop' && helperQuickActions ? (
              <div className="teamChatQuickActionWrap">
                <Pressable className="teamChatQuickActionFab" onClick={() => setIsQuickActionsOpen((current) => !current)} aria-expanded={isQuickActionsOpen}>+</Pressable>
                {isQuickActionsOpen ? (
                  <div className="teamChatQuickActionMenu">
                    {helperQuickActions.items && helperQuickActions.items.length > 0 ? (
                      <label className="teamChatQuickActionPicker">
                        <small>{t('NLSChatLabelTaskTag')}</small>
                        <Select
                          ariaLabel={t('NLSChatLabelTaskTag')}
                          value={helperQuickActions.selectedItemId ?? ''}
                          options={[{ value: '', label: t('NLSChatQuickPickOneTask') }, ...helperQuickActions.items.map((item) => ({ value: item.id, label: item.title }))]}
                          onChange={(value) => helperQuickActions.onSelectItem?.(value)}
                          trigger="pill"
                        />
                      </label>
                    ) : (
                      <small>{helperQuickActions.focusedLabel ? t('NLSChatQuickForTask', { label: helperQuickActions.focusedLabel }) : t('NLSChatQuickPickFocusedTask')}</small>
                    )}
                    <Button size="sm" variant="tertiary" onClick={() => helperEvidenceInputRef.current?.click()} disabled={!helperQuickActions.onEvidence}>{t('NLSChatActionEvidence')}</Button>
                    <Button size="sm" variant="tertiary" onClick={() => { void helperQuickActions.onScan?.() }} disabled={!helperQuickActions.onScan}>{t('NLSChatActionScan')}</Button>
                    <Button size="sm" variant="tertiary" onClick={() => { void helperQuickActions.onDone?.() }} disabled={!helperQuickActions.onDone}>{t('NLSChatActionDone')}</Button>
                    <Button
                      size="sm"
                      variant="tertiary"
                      onClick={() => {
                        setCommentPromptInput('')
                        setCommentPromptOpen(true)
                      }}
                      disabled={!helperQuickActions.onComment}
                    >
                      {t('NLSChatActionComment')}
                    </Button>
                  </div>
                ) : null}
                <HiddenFileInput
                  ref={helperEvidenceInputRef}
                  onFiles={(files) => {
                    const file = files[0]
                    if (file) void helperQuickActions.onEvidence?.(file)
                  }}
                />
              </div>
            ) : null}
            {mentionTrigger && (!isHelper || mentionTrigger.kind !== 'MEMBER') && (
              <MentionPopover
                kind={mentionTrigger.kind}
                members={mentionMemberMatches}
                elements={mentionElementMatches}
                onSelectMember={applyMemberMention}
                onSelectElement={applyElementMention}
              />
            )}

            <HiddenFileInput
              ref={fileInputRef}
              multiple
              onFiles={appendFiles}
            />
            <IconAction label={t('NLSChatActionAttachFiles')} onClick={() => fileInputRef.current?.click()} disabled={attachments.length >= MAX_ATTACHMENTS || isSending || !canSendInActiveChannel}>
              <AddCircleIcon sx={{ fontSize: 19 }} />
            </IconAction>
            <TextInput
              ref={inputRef}
              type="text"
              value={textInput}
              onChange={(val) => {
                setTextInput(val)
                void setTyping(Boolean(val.trim()))
              }}
              onKeyDown={handleKeyDown}
              disabled={!activeChannelId || !canSendInActiveChannel || isSending}
              placeholder={getComposerPlaceholder({ isActiveChannelArchived, canUseChat }, t)}
              wrapperClassName="teamChatComposerInputWrap"
            />
            <IconAction label={isRecordingAudio ? t('NLSChatActionStopAndSendAudio') : t('NLSChatActionRecordAudioMemo')} isActive={isRecordingAudio} onClick={toggleAudioRecording} disabled={isSending || !canSendInActiveChannel}>
              {isRecordingAudio ? <StopCircleIcon sx={{ fontSize: 18 }} /> : <GraphicEqIcon sx={{ fontSize: 18 }} />}
            </IconAction>
            <IconAction label={isDictating ? t('NLSChatActionStopDictation') : t('NLSChatActionDictateMessage')} isActive={isDictating} onClick={toggleDictation} disabled={isSending || !canSendInActiveChannel}>
              <KeyboardVoiceIcon sx={{ fontSize: 18 }} />
            </IconAction>
            <IconAction label={t('NLSChatActionSendMessage')} onClick={() => void submit()} disabled={isSending || !canSendInActiveChannel || (!textInput.trim() && attachments.length === 0 && mentionedElementIds.length === 0 && mentionedUserIds.length === 0 && entityReferences.length === 0)}>
              <SendIcon sx={{ fontSize: 18 }} />
            </IconAction>
          </form>
        </div>
      </main>

      {!chromeless ? (
      <aside className={`teamChatSidebar teamChatSidebar--right ${HIDE_SCROLLBAR}`}>
        <header className="teamChatSidebarHeader">
          <span>{t('NLSChatContextTitle')}</span>
          <IconButton
            size="sm"
            variant="tertiary"
            className="teamChatPaneClose"
            label={chatCopy.closeMembersLabel}
            icon={<CloseRoundedIcon fontSize="inherit" />}
            onClick={() => setMobilePane('main')}
          />
        </header>
        {selectedEntityReference ? (
          <ChatEntitySidecar
            reference={selectedEntityReference}
            element={selectedElement}
            checklistItem={selectedChecklistItem}
            canEditElement={canPerformOnNode('timeline.edit', selectedElement?.id ?? selectedEntityReference.entityId)}
            canEditChecklist={canPerformOnNode('checklist.edit_canvas', selectedChecklistItem?.id ?? selectedEntityReference.entityId)}
            onClose={() => setSelectedEntityReference(null)}
            onOpenFull={() => {
              if (selectedEntityReference.entityType === 'ELEMENT' || selectedEntityReference.entityType === 'CHECKLIST') {
                onElementMentionClick?.(selectedEntityReference.entityId)
              }
            }}
            onUpdateElement={(updates) => updateElement(selectedEntityReference.entityId, updates)}
            onUpdateChecklist={(updates) => updateChecklistItem(selectedEntityReference.entityId, updates)}
          />
        ) : null}
        {!isHelper && (
          <div className="teamChatSidebarSection">
            <p className="dropdownLabel">{t('NLSChatLabelMembersCount', { count: members.length })}</p>
            {members.map((member) => (
              <RosterRow key={member.id} member={member} />
            ))}
          </div>
        )}
      </aside>
      ) : null}
      <AppDialog
        open={Boolean(pendingDeleteChannel)}
        title={t('NLSChatDialogArchiveChannel')}
        message={pendingDeleteChannel ? t('NLSChatDialogArchiveChannelMessage', { channel: pendingDeleteChannel.slug || pendingDeleteChannel.name }) : ''}
        onDismiss={() => setPendingDeleteChannel(null)}
        actions={[
          { label: t('NLSChatActionCancel'), variant: 'ghost', onClick: () => setPendingDeleteChannel(null) },
          { label: t('NLSChatActionArchive'), variant: 'danger', onClick: confirmDeleteChannel },
        ]}
      />
      <AppDialog
        open={Boolean(pendingPermanentDeleteChannel)}
        title={t('NLSChatDialogDeleteForever')}
        message={pendingPermanentDeleteChannel ? t('NLSChatDialogDeleteForeverMessage', { channel: pendingPermanentDeleteChannel.slug || pendingPermanentDeleteChannel.name }) : ''}
        onDismiss={() => setPendingPermanentDeleteChannel(null)}
        actions={[
          { label: t('NLSChatActionCancel'), variant: 'ghost', onClick: () => setPendingPermanentDeleteChannel(null) },
          { label: t('NLSChatActionDeleteForever'), variant: 'danger', onClick: confirmPermanentDeleteChannel },
        ]}
      />
      <AppDialog
        open={Boolean(editingMessage)}
        title={t('NLSChatDialogEditMessage')}
        message={(
          <label className="teamChatEditDialogField">
            <span>{t('NLSChatLabelMessage')}</span>
            <Textarea
              value={editingMessageText}
              onChange={setEditingMessageText}
              rows={5}
              maxLength={2000}
              autoFocus
            />
          </label>
        )}
        onDismiss={() => {
          setEditingMessage(null)
          setEditingMessageText('')
        }}
        actions={[
          { label: t('NLSChatActionCancel'), variant: 'ghost', onClick: () => { setEditingMessage(null); setEditingMessageText('') } },
          { label: t('NLSChatActionSaveEdit'), variant: 'primary', onClick: submitEditMessage },
        ]}
      />
      {commentPromptOpen && (
        <AppDialog
          open={commentPromptOpen}
          title={t('NLSChatActionComment')}
          onDismiss={() => {
            setCommentPromptOpen(false)
            setCommentPromptInput('')
          }}
          actions={commentPromptActions}
        >
          <div className="teamChatCommentPromptField">
            <TextInput
              value={commentPromptInput}
              onChange={setCommentPromptInput}
              placeholder={t('NLSChatActionComment')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCommentSubmit()
                }
              }}
            />
          </div>
        </AppDialog>
      )}
      <AttachmentPreviewDialog
        open={Boolean(previewItem)}
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        onAnnotate={platform === 'desktop' && canPerform('forms.design') ? annotatePreviewItem : undefined}
      />
    </div>
    </ConditionalRailShell>
  )
}

const ConditionalRailShell = ({ wrap, ariaLabel, children }: { wrap: boolean; ariaLabel?: string; children: ReactNode }) => {
  if (!wrap) return <>{children}</>
  return <RailPageShell variant="full" ariaLabel={ariaLabel}>{children}</RailPageShell>
}

const IconAction = ({
  label,
  children,
  isActive = false,
  disabled = false,
  onClick,
}: {
  label: string
  children: ReactNode
  isActive?: boolean
  disabled?: boolean
  onClick: () => void
}) => (
  <IconButton
    size="sm"
    variant="tertiary"
    label={label}
    icon={children}
    pressed={isActive}
    disabled={disabled}
    onClick={onClick}
  />
)

const ChatEntitySidecar = ({
  reference,
  element,
  checklistItem,
  canEditElement,
  canEditChecklist,
  onClose,
  onOpenFull,
  onUpdateElement,
  onUpdateChecklist,
}: {
  reference: EntityReference
  element: TimelineElement | null
  checklistItem: ChecklistItem | null
  canEditElement: boolean
  canEditChecklist: boolean
  onClose: () => void
  onOpenFull: () => void
  onUpdateElement: (updates: Partial<TimelineElement>) => Promise<void>
  onUpdateChecklist: (updates: Partial<ChecklistItem>) => Promise<void>
}) => {
  const { t } = useNls()
  const targetTitle = String(element?.title ?? checklistItem?.title ?? reference.snapshot.title ?? t('NLSChatEntityWorkspaceItem'))
  const targetSubtitle = String(element
    ? `${element.type} · ${element.boardStatus ?? 'TODO'}`
    : checklistItem
      ? `${t('NLSChatLabelChecklist')} · ${checklistItem.workflowStatus ?? checklistItem.boardStatus ?? 'CREATED'}`
      : reference.snapshot.subtitle ?? reference.entityType
  )
  const canEdit = reference.entityType === 'ELEMENT' ? canEditElement : reference.entityType === 'CHECKLIST' ? canEditChecklist : false
  const [titleDraft, setTitleDraft] = useState(String(targetTitle))
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setTitleDraft(String(targetTitle))
  }, [targetTitle])

  const saveTitle = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canEdit || reference.entityType !== 'ELEMENT' || !element) return
    const nextTitle = titleDraft.trim()
    if (!nextTitle || nextTitle === element.title) return
    setIsSaving(true)
    try {
      await onUpdateElement({ title: nextTitle })
    } finally {
      setIsSaving(false)
    }
  }

  const setElementStatus = async (status: TimelineElement['boardStatus']) => {
    if (!canEdit || reference.entityType !== 'ELEMENT' || !element) return
    setIsSaving(true)
    try {
      await onUpdateElement({ boardStatus: status, workflowStatus: status })
    } finally {
      setIsSaving(false)
    }
  }

  const setChecklistStatus = async (status: ChecklistItem['workflowStatus']) => {
    if (!canEdit || reference.entityType !== 'CHECKLIST' || !checklistItem) return
    setIsSaving(true)
    try {
      await onUpdateChecklist({ workflowStatus: status, isCompleted: status === 'COMPLETED' })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="chatEntitySidecar">
      <header>
        <span>{reference.entityType === 'USER' ? t('NLSChatEntityMemberContext') : t('NLSChatEntityContext')}</span>
        <IconButton
          size="sm"
          variant="tertiary"
          label={t('NLSChatActionCloseEntityContext')}
          icon={<CloseRoundedIcon fontSize="inherit" />}
          onClick={onClose}
        />
      </header>
      <div className="chatEntitySidecarBody">
        <strong>{targetTitle}</strong>
        <small>{targetSubtitle}</small>
        {reference.entityType === 'ELEMENT' && element && canEdit ? (
          <>
            <form onSubmit={saveTitle} className="chatEntitySidecarForm">
              <label>
                <span>{t('NLSChatEntityTitle')}</span>
                <TextInput value={titleDraft} onChange={setTitleDraft} disabled={isSaving} />
              </label>
              <Button
                type="submit"
                size="sm"
                variant="tertiary"
                disabled={isSaving || !titleDraft.trim()}
              >
                {t('NLSChatActionSave')}
              </Button>
            </form>
            <div className="chatEntitySidecarActions" aria-label={t('NLSChatEntityTimelineStatusActions')}>
              {(['TODO', 'IN_PROGRESS', 'COMPLETE'] as const).map((status) => (
                <Pressable
                  key={status}
                  className={`topbarPill topbarPill--sm${element.boardStatus === status ? ' isActive' : ''}`}
                  disabled={isSaving}
                  onClick={() => { void setElementStatus(status) }}
                  aria-pressed={element.boardStatus === status}
                >
                  {status === 'TODO' ? t('NLSChatStatusTodo') : status === 'IN_PROGRESS' ? t('NLSChatStatusInProgress') : t('NLSChatStatusComplete')}
                </Pressable>
              ))}
            </div>
          </>
        ) : null}
        {reference.entityType === 'CHECKLIST' && checklistItem && canEdit ? (
          <div className="chatEntitySidecarActions" aria-label={t('NLSChatEntityChecklistStatusActions')}>
            {(['ONGOING', 'COMPLETED', 'POSTPONED'] as const).map((status) => (
              <Pressable
                key={status}
                className={`topbarPill topbarPill--sm${checklistItem.workflowStatus === status ? ' isActive' : ''}`}
                disabled={isSaving}
                onClick={() => { void setChecklistStatus(status) }}
                aria-pressed={checklistItem.workflowStatus === status}
              >
                {status === 'ONGOING' ? t('NLSChatStatusOngoing') : status === 'COMPLETED' ? t('NLSChatStatusCompleted') : t('NLSChatStatusPostponed')}
              </Pressable>
            ))}
          </div>
        ) : null}
        {!canEdit ? <p className="chatEntitySidecarReadOnly">{t('NLSChatEntityReadOnly')}</p> : null}
        <Button size="sm" variant="tertiary" onClick={onOpenFull}>
          {t('NLSChatActionOpenFullDetail')}
        </Button>
      </div>
    </section>
  )
}

const RosterRow = ({ member }: { member: ChatMember }) => {
  const { t } = useNls()
  return (
    <Pressable className="teamChatSidebarRow" style={{ alignItems: 'center', justifyContent: 'flex-start', gap: 10, minHeight: 40 }}>
      {member.avatarUrl ? (
        <img src={member.avatarUrl} alt="" className="teamChatAvatar" style={{ width: 28, height: 28 }} />
      ) : (
        <span className="teamChatAvatarFallback" style={{ width: 28, height: 28, fontSize: 11 }}>
          {initialsOf(member.name)}
        </span>
      )}
      <span style={{ display: 'grid', minWidth: 0, lineHeight: 1.15 }}>
        <span className={`${ROLE_TEXT_COLOR[member.role]}`} style={{ fontSize: 12, fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {member.name}
        </span>
        <span style={{ color: 'var(--muted)', fontSize: 10, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {getChatRoleLabel(t, member.role)}
        </span>
      </span>
    </Pressable>
  )
}
